import { useState, useEffect, useMemo } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "./styles.module.css";

interface ManpowerCostFlowDecisionProps {
  parsedContent?: any;
  messageId?: string;
}

export default function ManpowerCostFlowDecision({
  parsedContent,
  messageId,
}: ManpowerCostFlowDecisionProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const loading = useSelector((state: RootState) => state.user.loading);
  const currentChat = useSelector((state: RootState) => state.user.currentChat);

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [wasLoading, setWasLoading] = useState(false);

  // Check if this is a success message (completion flow)
  const successMessage = parsedContent?.finalAnswer?.message;
  // Extract question text from parsed content
  const questionText =
    parsedContent?.question ||
    parsedContent?.finalAnswer?.question ||
    "Would you like to start a new estimation, or continue an existing one?";

  // Session ID for API calls
  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("sessionId");
  }, []);

  // Check if this is the latest message
  const isLatestMessage = useMemo(() => {
    if (!messageId) return true;

    const lastAgentMessage = [...currentChat]
      .reverse()
      .find((msg) => msg.sender === "agent");

    return lastAgentMessage?.id === messageId;
  }, [currentChat, messageId]);

  // Reset selection state when loading completes
  useEffect(() => {
    if (wasLoading && !loading) {
      setSelectedOption(null);
    }
    setWasLoading(loading);
  }, [loading, wasLoading]);

  // Handle button click
  const handleOptionClick = (option: string, message: string) => {
    if (selectedOption || loading || !currentAgent) return;

    setSelectedOption(option);

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent.id,
        message,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent.endpoint,
        sessionId,
      }),
    );
  };

  return (
    <Box className={styles.container}>
      {/* Success Message */}
      {successMessage && (
        <Box className={styles.successContainer}>
          <CheckCircle className={styles.successIcon} />
          <Typography className={styles.successMessage}>
            {successMessage}
          </Typography>
        </Box>
      )}
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerIcon}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#00A3A3">
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Box>
        <Typography variant="subtitle2" className={styles.headerText}>
          MANPOWER COST CALCULATOR
        </Typography>
      </Box>

      {/* Question */}
      <Typography className={styles.question}>{questionText}</Typography>

      {/* Action Buttons */}
      {isLatestMessage && (
        <Stack direction="row" spacing={2} className={styles.buttonContainer}>
          <Button
            variant="contained"
            onClick={() =>
              handleOptionClick("new_estimation", "New estimation")
            }
            disabled={selectedOption !== null || loading}
            className={styles.primaryButton}
          >
            New Estimation
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              handleOptionClick("existing_estimation", "Existing estimation")
            }
            disabled={selectedOption !== null || loading}
            className={styles.secondaryButton}
          >
            Existing Estimation
          </Button>
        </Stack>
      )}
    </Box>
  );
}
